import { Overlay } from '@design/Overlay'
import { useCallback, useEffect, useState } from 'react'
import { formatCents } from '@core/money'
import { loadPayables, type Payable } from '@core/payments'
import {
  finishRun,
  loadOpenRun,
  nextPending,
  rescheduleItem,
  settleItem,
  summarise,
  type PaymentRun,
} from '@core/paymentRuns'
import { Button, Card, ErrorNote, Screen, Spinner } from '@design/components'
import { PaymentCard } from './PaymentCard'

/**
 * Working through a run, one card at a time.
 *
 * spec.md §13 is unusually specific about this screen, and for good reason:
 * "the moment you leave the app for your bank is the highest-friction, most
 * error-prone, most data-destroying point in the whole product."
 *
 * Two details from it that look cosmetic and are not:
 *
 * Skip and Reschedule are different buttons because they are different
 * intents. Skip keeps the item in the run for later in the same sitting;
 * reschedule takes it out with a new date. Collapsing them would mean
 * guessing which one someone meant.
 *
 * Progress is read from the database, not from React state. Coming back to
 * card one of seven after an interruption is exactly how people stop trusting
 * the numbers.
 */
export function RunScreen({
  spaceId,
  userId,
  onClose,
}: {
  spaceId: string
  userId: string
  onClose: () => void
}) {
  const [run, setRun] = useState<PaymentRun | null | undefined>(undefined)
  const [payables, setPayables] = useState<Payable[]>([])
  const [busy, setBusy] = useState(false)
  const [rescheduling, setRescheduling] = useState(false)
  const [newDate, setNewDate] = useState('')

  const refresh = useCallback(async () => {
    const [loaded, items] = await Promise.all([loadOpenRun(spaceId), loadPayables()])
    setPayables(items)
    setRun(loaded)
  }, [spaceId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Every branch below is inside the Overlay, including the ones that return
  // early. They used not to be: a payment run that finished, or was still
  // loading, rendered inline inside the list it was started from — a full
  // payment-run summary squeezed between the tab bar and a filter row, with
  // its "Klaar" button wherever that happened to put it.
  if (run === undefined) {
    return (
      <Overlay>
        <Screen className="grid place-items-center pad-safe-top">
          <Spinner label="Even geduld…" />
        </Screen>
      </Overlay>
    )
  }
  if (run === null) {
    return (
      <Overlay>
        <Screen className="grid place-items-center pad-safe-top">
          <p className="text-muted mb-4">Er loopt geen betaalronde.</p>
          <Button tone="secondary" onClick={onClose}>
            Terug
          </Button>
        </Screen>
      </Overlay>
    )
  }

  const item = nextPending(run)
  const summary = summarise(run, payables)
  const payable = item ? payables.find((p) => p.instalmentId === item.instalmentId) : undefined

  if (!item || !payable) {
    return (
      <Overlay>
        <RunComplete run={run} userId={userId} onClose={onClose} />
      </Overlay>
    )
  }

  async function act(fn: () => Promise<void>) {
    setBusy(true)
    try {
      await fn()
      await refresh()
    } finally {
      setBusy(false)
      setRescheduling(false)
    }
  }

  return (
    <Overlay>
      <div className="shrink-0 pad-safe-top px-4 pt-2">
        <div className="flex items-center justify-between mb-1">
          <button onClick={onClose} className="text-muted py-2 px-1 -ml-1">
            ← Pauzeer
          </button>
          <span className="text-sm text-muted tabular-nums">
            {summary.done + 1} van {summary.total} · {formatCents(summary.remainingCents)} te gaan
          </span>
        </div>
        <Dots total={summary.total} done={summary.done} />
      </div>

      <div className="flex-1 min-h-0 relative">
        {/* The ordinary payment card, unchanged. The run is an optimisation
            around it, not a different way to pay — §13's "solo-payment path"
            has to keep working identically. */}
        <PaymentCard
          payable={payable}
          userId={userId}
          onClose={onClose}
          onPaid={() => void act(() => settleItem(run, item, 'paid'))}
        />
      </div>

      <div className="shrink-0 px-4 pb-4 pad-safe-bottom border-t border-line bg-canvas">
        {rescheduling ? (
          <div className="pt-3">
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line text-lg mb-2"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => void act(() => rescheduleItem(run, item, newDate))}
                disabled={busy || !newDate}
              >
                Verplaatsen
              </Button>
              <Button tone="quiet" onClick={() => setRescheduling(false)}>
                Terug
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 pt-3">
            <Button
              tone="secondary"
              onClick={() => void act(() => settleItem(run, item, 'skipped'))}
              disabled={busy}
            >
              Overslaan
            </Button>
            <Button tone="quiet" onClick={() => setRescheduling(true)} disabled={busy}>
              Verplaatsen
            </Button>
          </div>
        )}
      </div>
    </Overlay>
  )
}

/** Progress as dots, because a percentage of seven invoices means nothing. */
function Dots({ total, done }: { total: number; done: number }) {
  return (
    <div className="flex gap-1 mb-2" aria-label={`${done} van ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`h-1.5 flex-1 rounded-full ${i < done ? 'bg-ink' : 'bg-line'}`}
        />
      ))}
    </div>
  )
}

/**
 * The end of a run.
 *
 * spec.md §13: the line about the parent having been told "is not decoration.
 * It's the moment the app proves it closed the loop." Kept.
 */
function RunComplete({
  run,
  userId,
  onClose,
}: {
  run: PaymentRun
  userId: string
  onClose: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const paid = run.items.filter((i) => i.state === 'paid').length
  const rescheduled = run.items.filter((i) => i.state === 'rescheduled').length
  const skipped = run.items.filter((i) => i.state === 'skipped').length

  return (
    <Screen className="flex flex-col justify-center max-w-md mx-auto w-full text-center pad-safe-top">
      <p className="text-5xl mb-4">✓</p>
      <h1 className="text-xl font-semibold mb-4">Betaalronde klaar</h1>

      <Card className="text-left mb-4">
        {paid > 0 && <p className="mb-1">{paid} betaald</p>}
        {rescheduled > 0 && <p className="mb-1 text-muted">{rescheduled} verplaatst</p>}
        {skipped > 0 && <p className="mb-1 text-muted">{skipped} overgeslagen</p>}
        {paid > 0 && (
          <p className="text-sm text-muted mt-3">
            Wie de facturen stuurde, heeft bericht gekregen.
          </p>
        )}
      </Card>

      <Button
        size="large"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          try {
            await finishRun(run, userId)
            onClose()
          } catch (err) {
            // Without this the button stays disabled for good and the only way
            // out of a full-screen payment run is to kill the app.
            setError(err instanceof Error ? err.message : 'Afsluiten lukte niet.')
            setBusy(false)
          }
        }}
      >
        Klaar
      </Button>

      <ErrorNote>{error}</ErrorNote>
    </Screen>
  )
}
